import { Typography, Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@emotion/react';
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import responsiveFont from '../utils/responsiveFont';
import auth from '../service/authRepository';
import { setClientInfo } from '../store/reports';
import Loader from '../components/loader';
import Footer from './footer';
import { DataGrid } from '@mui/x-data-grid';
import { ExpandLessOutlined, ExpandMoreOutlined, UnfoldMoreOutlined } from '@mui/icons-material';

export function SortedDescendingIcon() {
  return <ExpandMoreOutlined className="icon" sx={{ color: '#666666' }} />;
}

export function SortedAscendingIcon() {
  return <ExpandLessOutlined className="icon" sx={{ color: '#666666' }} />;
}

export function UnsortedIcon() {
  return <UnfoldMoreOutlined className="icon" sx={{ color: '#666666' }} />;
}

const SubmitStatus = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mdMatches = useMediaQuery((theme) => theme.breakpoints.up('md'));
  const lgMatches = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const xlMatches = useMediaQuery((theme) => theme.breakpoints.up('xl'));
  const [rows, setRows] = React.useState([]);
  // Heading above the results table — defaults to a generic line and is replaced
  // by the API's statusInfo.statusMsg whenever the response carries per-item
  // results (SUCCESS, PARTIAL_SUCCESS, or FAIL with item-level detail).
  const [resultMessage, setResultMessage] = React.useState(
    'Your report actions have been submitted. See the status of each item below.',
  );
  // True while the submit call is in flight, so the user sees a loader instead
  // of an empty grid.
  const [isLoading, setIsLoading] = React.useState(true);
  // The token captured at verification; the service derives uid/sid from it.
  const submitToken = useSelector((state) => state.reports.submitToken);
  // Guards the submit against React StrictMode's double-invocation of effects
  // in dev — this is a write, so it must fire at most once.
  const hasSubmittedRef = React.useRef(false);

  const commonStyles = {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    wordBreak: 'break-word',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    color: '#000000',
    fontSize: responsiveFont(13, mdMatches),
    fontWeight: 'regular',
    minHeight: '40px',
  };

  const getPaginationWidth = () => {
    if (xlMatches) return '62.5%';
    if (lgMatches) return '65%';
    return '65%';
  };

  const columns = [
    {
      field: 'controlNo',
      headerName: 'Control Number',
      flex: 1,
      renderCell: (params) => (
        <Box sx={commonStyles}>{params.value === '' ? '-' : params.value}</Box>
      ),
    },
    {
      field: 'reportAction',
      headerName: 'Report Action',
      flex: 1,
      renderCell: (params) => (
        <Box sx={commonStyles}>{params.value === '' ? '-' : params.value}</Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.7,
      renderCell: (params) => (
        <Box
          sx={{
            ...commonStyles,
            fontWeight: 900,
            // CrudLite ACTION_STATUS: PASS = success (green), anything else
            // (FAIL, INELIGIBLE, ...) = red.
            color: params.value?.toUpperCase() === 'PASS' ? '#2E7D32' : theme.palette.error.main,
          }}
        >
          {params.value === '' ? '-' : params.value}
        </Box>
      ),
    },
  ];

  React.useEffect(() => {
    document.title = 'SUBMIT STATUS';
    // Nothing to submit without a token (e.g. direct navigation) — stop loading.
    if (!submitToken) {
      setIsLoading(false);
      return;
    }
    // Fire the submit only once, even if the effect runs twice (StrictMode's
    // dev double-invoke). No `active`/cleanup flag: the ref already guarantees a
    // single fetch, and a cleanup flag would let StrictMode's cleanup of the
    // first run discard the (single) response and leave the loader stuck.
    if (hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;

    // The report-action-all endpoint takes only the token; the service derives
    // the transaction id (uid) and sid from its claims.
    const submitPayload = { token: submitToken };
    (async () => {
      // The service catch-and-returns the axios error, so a FAIL body can arrive
      // under response.data (2xx business failure) or response.response.data
      // (non-2xx). Normalize both before branching.
      const response = await auth.saveReportActionAll(submitPayload);
      const body = response?.data ?? response?.response?.data;

      // Any response that carries per-item results shows the table, regardless
      // of the overall statusCode (SUCCESS, PARTIAL_SUCCESS, or a FAIL that still
      // returns item-level detail).
      const list = body?.reportActionAllStatusList;
      if (Array.isArray(list) && list.length > 0) {
        if (body?.statusInfo?.statusMsg) {
          setResultMessage(body.statusInfo.statusMsg);
        }
        // Seed clientInfo so the shared Header shows the account name in the same
        // place/style as the request page (top-right, under "Go to MY LAB").
        if (body?.clientInfo) {
          dispatch(setClientInfo(body.clientInfo));
        }
        // Surface any non-PASS items (FAIL, INELIGIBLE, ...) at the top so the
        // client notices them first. Array.sort is stable, so PASS rows keep
        // their original order and an all-PASS list is left untouched.
        const sortedList = [...list].sort((a, b) => {
          const aFailed = (a.status || '').toUpperCase() !== 'PASS' ? 1 : 0;
          const bFailed = (b.status || '').toUpperCase() !== 'PASS' ? 1 : 0;
          return bFailed - aFailed;
        });
        setRows(sortedList);
        setIsLoading(false);
        return;
      }
      // FAIL with no item detail (e.g. "This link has already been used.") — send
      // the user to the shared warning page with the server's message.
      if (body?.statusInfo?.statusCode === 'FAIL') {
        navigate('/status', {
          state: { status: 'WARNING', message: body.statusInfo.statusMsg },
        });
        return;
      }
      // No usable body (network/HTTP error or unexpected shape) — generic
      // fallback, matching the wording used in login.jsx / confirmation.jsx.
      navigate('/status', {
        state: {
          status: 'WARNING',
          message: 'Your requested report actions were unable to be processed.',
        },
      });
    })();
  }, [submitToken, navigate, dispatch]);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '4%' }}>
        <Box
          sx={{
            backgroundColor: theme.palette.menubar.main,
            width: '91%',
            height: responsiveFont(45, mdMatches),
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              color: '#fff',
              fontSize: responsiveFont(20, mdMatches),
              marginLeft: '1%',
            }}
          >
            SUBMIT STATUS
          </Box>
        </Box>
      </Box>

      {isLoading ? (
        <Box mt={4} textAlign="center">
          <Typography
            sx={{
              fontWeight: '900',
              fontSize: responsiveFont(16, mdMatches),
              color: '#000000',
            }}
          >
            Submitting your request. Please wait…
          </Typography>
          <Loader />
        </Box>
      ) : (
        <Box mt={4} textAlign="center">
          <Typography
            sx={{
              fontWeight: '900',
              fontSize: responsiveFont(16, mdMatches),
              color: '#000000',
            }}
          >
            {resultMessage}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              height: '445px',
              justifyContent: 'center',
              marginTop: '1%',
            }}
          >
            <Box
              sx={{
                width: '91%',
                boxShadow: rows.length > 9 ? 'inset 0 -10px 10px -10px rgba(0,0,0,0.5)' : 0,
              }}
            >
              <DataGrid
                getRowId={(data) => data.controlNo}
                rows={rows}
                disableColumnResize
                disableColumnMenu
                columns={columns}
                slots={{
                  columnSortedDescendingIcon: SortedDescendingIcon,
                  columnSortedAscendingIcon: SortedAscendingIcon,
                  columnUnsortedIcon: UnsortedIcon,
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 100 } },
                }}
                pageSizeOptions={[50, 100]}
                sx={{
                  '& .MuiTablePagination-root': {
                    width: getPaginationWidth,
                    display: 'flex',
                    justifyContent: 'start',
                  },
                  '& .MuiDataGrid-columnHeaderTitleContainerContent .MuiButtonBase-root': {
                    backgroundColor: '#fff',
                    width: '18px',
                    height: '18px',
                  },
                  '& .MuiTablePagination-actions .MuiSvgIcon-root': {
                    color: '#000 !important',
                  },
                  '& .MuiInputBase-input': {
                    color: '#000',
                  },
                  '& .MuiInputBase-root .MuiSvgIcon-root': {
                    color: '#000 !important',
                  },
                  '& .Mui-focused': {
                    color: '#000',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    whiteSpace: 'normal',
                    lineHeight: 'normal',
                    fontWeight: 900,
                  },
                  border: '0',
                  '--unstable_DataGrid-radius': '0px',
                  '.MuiDataGrid-columnHeader': {
                    backgroundColor: '#EEEEEE',
                    color: '#000000',
                    fontSize: responsiveFont(13, mdMatches),
                    '& .MuiDataGrid-iconSeparator': {
                      display: 'none',
                    },
                  },
                }}
                getRowHeight={() => 'auto'}
                getEstimatedRowHeight={() => 200}
              />
            </Box>
          </Box>

          <Typography
            sx={{
              fontSize: responsiveFont(16, mdMatches),
              color: '#000000',
              marginTop: '1%',
            }}
          >
            If you need to make changes or have questions, please log in to MY LAB or contact your
            Client Service Representative for further assistance.
          </Typography>
        </Box>
      )}
      {!isLoading && <Footer />}
    </>
  );
};

export default SubmitStatus;
